import React from 'react';

import { Alert, Descriptions, Flex } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { ICertificate } from '@app/services/cades/interfaces';
import { formatDate } from '@app/services/date';

interface IProps {
  certificate: ICertificate;
  showAttributes?: boolean;
}

export const CertificateDetails: React.FC<IProps> = ({ certificate, showAttributes = false }) => {
  const cadesT = useTranslation(NSKeys.cades);
  const commonT = useTranslation(NSKeys.common);
  const { identity } = certificate;
  const flags = certificate.errorStatuses.reduce((value, status) => value | status, 0);
  const reasons: string[] = [];
  if (flags & 1) reasons.push(cadesT.t('invalidTime'));
  if (flags & 4) reasons.push(cadesT.t('revoked'));
  if (flags & (32 | 65536)) reasons.push(cadesT.t('missingRoots'));
  if (!certificate.hasPrivateKey) reasons.push(cadesT.t('noPrivateKey'));
  if (!certificate.valid && !reasons.length) reasons.push(cadesT.t('hints.certificates'));
  return (
    <Flex vertical gap={8}>
      <Descriptions
        size="small"
        column={1}
        items={[
          {
            key: 'owner',
            label: cadesT.t('owner'),
            children:
              [identity.lastName, identity.firstName, identity.middleName].filter(Boolean).join(' ') ||
              identity.commonName,
          },
          { key: 'issuer', label: cadesT.t('issuer'), children: certificate.issuer },
          { key: 'serial', label: cadesT.t('serial'), children: certificate.serial },
          {
            key: 'period',
            label: cadesT.t('period'),
            children: `${formatDate(certificate.validFrom)} — ${formatDate(certificate.validTo)}`,
          },
          { key: 'valid', label: cadesT.t('valid'), children: commonT.t(certificate.valid ? 'yes' : 'no') },
          ...(showAttributes
            ? [
                { key: 'snils', label: 'SNILS', children: identity.snils || '—' },
                { key: 'inn', label: 'INN', children: identity.inn || '—' },
                { key: 'innLe', label: 'INNLE', children: identity.innLe || '—' },
                { key: 'sn', label: 'SN', children: identity.lastName || '—' },
                {
                  key: 'g',
                  label: 'G',
                  children: [identity.firstName, identity.middleName].filter(Boolean).join(' ') || '—',
                },
                { key: 't', label: 'T', children: identity.position || '—' },
                {
                  key: 'errorStatuses',
                  label: 'ErrorStatuses',
                  children:
                    certificate.statusError ||
                    certificate.errorStatuses.map((value) => `0x${value.toString(16).toUpperCase()}`).join(', ') ||
                    '—',
                },
              ]
            : []),
        ]}
      />
      {!!reasons.length && (
        <Alert
          type="warning"
          showIcon
          title={reasons.join('; ')}
          description={certificate.error || certificate.statusError}
        />
      )}
    </Flex>
  );
};
